import {
    BugReport as BugIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
    DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
    Chip,
} from '@mui/material';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebugData } from '../contexts/DebugDataContext';

interface Position {
    x: number;
    y: number;
}

const DebugDataWindow = () => {
    const { t } = useTranslation();
    const {
        runtimeData,
        isDebugWindowOpen,
        setIsDebugWindowOpen,
        refreshSessionData,
        isRefreshing,
    } = useDebugData();
    const [position, setPosition] = useState<Position>({ x: 20, y: 80 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
    const windowRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = useCallback((event: React.MouseEvent) => {
        if (windowRef.current) {
            const rect = windowRef.current.getBoundingClientRect();
            setDragOffset({
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
            });
            setIsDragging(true);
        }
    }, []);

    const handleMouseMove = useCallback(
        (event: MouseEvent) => {
            if (isDragging) {
                const newX = event.clientX - dragOffset.x;
                const newY = event.clientY - dragOffset.y;

                // Keep window within viewport bounds
                const maxX = window.innerWidth - (windowRef.current?.offsetWidth ?? 300);
                const maxY = window.innerHeight - (windowRef.current?.offsetHeight ?? 400);

                setPosition({
                    x: Math.max(0, Math.min(newX, maxX)),
                    y: Math.max(0, Math.min(newY, maxY)),
                });
            }
        },
        [isDragging, dragOffset],
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    if (!isDebugWindowOpen) {
        return null;
    }

    const formatValue = (value: unknown): string => {
        if (value === null || value === undefined) {
            return '-';
        }
        if (typeof value === 'number') {
            return value.toLocaleString();
        }
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        if (typeof value === 'string') {
            return value;
        }
        return String(value as boolean | bigint | symbol);
    };

    const formatTimestamp = (timestamp: string | null): string => {
        if (!timestamp) return '-';
        try {
            const date = new Date(timestamp);
            return date.toLocaleTimeString();
        } catch {
            return timestamp;
        }
    };

    const getStatusColor = (status: string | null): 'default' | 'success' | 'warning' | 'error' => {
        if (!status) return 'default';
        switch (status) {
            case '':
            case 'active':
                return 'success';
            case '!':
            case 'passive':
                return 'warning';
            case '!!':
            case 'expired':
                return 'error';
            case 'INV':
            case 'EXP':
            case 'stock':
                return 'default';
            default:
                return 'default';
        }
    };

    const formatBooleanValue = (value: boolean | null): string => {
        if (value === null) return '-';
        return value ? t('common.yes') : t('common.no');
    };

    const dataRows = [
        {
            label: t('debug.clientId'),
            value: formatValue(runtimeData.currentClientId),
            section: 'client',
        },
        {
            label: t('debug.clientName'),
            value: formatValue(runtimeData.currentClientName),
            section: 'client',
        },
        {
            label: t('debug.clientCNP'),
            value: formatValue(runtimeData.currentClientCNP),
            section: 'client',
        },
        {
            label: t('debug.contractNumber'),
            value: formatValue(runtimeData.currentContractNumber),
            section: 'contract',
        },
        {
            label: t('debug.contractStatus'),
            value: runtimeData.currentContractStatus,
            section: 'contract',
            isStatus: true,
        },
        {
            label: t('debug.contractValue'),
            value: runtimeData.currentContractValue
                ? `${Math.round(runtimeData.currentContractValue).toLocaleString()} RON`
                : '-',
            section: 'contract',
        },
        {
            label: t('debug.cashSessionId'),
            value: formatValue(runtimeData.cashSessionId),
            section: 'session',
        },
        {
            label: t('debug.cashSessionNumber'),
            value: formatValue(runtimeData.cashSessionNumber),
            section: 'session',
        },
        {
            label: t('debug.cashPointId'),
            value: formatValue(runtimeData.cashPointId),
            section: 'session',
        },
        {
            label: t('debug.cashPointName'),
            value: formatValue(runtimeData.cashPointName),
            section: 'session',
        },
        {
            label: t('debug.sessionStartTime'),
            value: formatTimestamp(runtimeData.sessionStartTime),
            section: 'session',
        },
        {
            label: t('debug.isNewSession'),
            value: formatBooleanValue(runtimeData.isNewSession),
            section: 'session',
        },
        {
            label: t('debug.cashDrawerBalance'),
            value:
                runtimeData.cashDrawerBalance !== null
                    ? `${Math.round(runtimeData.cashDrawerBalance).toLocaleString()} RON`
                    : '-',
            section: 'session',
        },
        { label: t('debug.userId'), value: formatValue(runtimeData.userId), section: 'user' },
        { label: t('debug.username'), value: formatValue(runtimeData.username), section: 'user' },
        { label: t('debug.userRole'), value: formatValue(runtimeData.userRole), section: 'user' },
        {
            label: t('debug.serverTime'),
            value: formatTimestamp(runtimeData.serverTime),
            section: 'system',
        },
        {
            label: t('debug.lastRefresh'),
            value: formatTimestamp(runtimeData.lastRefresh),
            section: 'system',
        },
    ];

    return (
        <Paper
            ref={windowRef}
            elevation={8}
            sx={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                zIndex: 9999,
                minWidth: 320,
                maxWidth: 400,
                backgroundColor: 'background.paper',
                border: '2px solid',
                borderColor: 'warning.main',
                borderRadius: 1,
                overflow: 'hidden',
                userSelect: isDragging ? 'none' : 'auto',
            }}
        >
            {/* Header - Draggable */}
            <Box
                onMouseDown={handleMouseDown}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: 'warning.main',
                    color: 'warning.contrastText',
                    px: 1,
                    py: 0.5,
                    cursor: isDragging ? 'grabbing' : 'grab',
                }}
            >
                <Box display="flex" alignItems="center" gap={0.5}>
                    <DragIcon fontSize="small" />
                    <BugIcon fontSize="small" />
                    <Typography variant="subtitle2" fontWeight="bold">
                        {t('debug.title')}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <Tooltip title={t('debug.refresh')}>
                        <IconButton
                            size="small"
                            onClick={event => {
                                event.stopPropagation();
                                refreshSessionData();
                            }}
                            disabled={isRefreshing}
                            sx={{ color: 'inherit' }}
                        >
                            <RefreshIcon
                                fontSize="small"
                                sx={{
                                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                                    '@keyframes spin': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' },
                                    },
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('debug.close')}>
                        <IconButton
                            size="small"
                            onClick={event => {
                                event.stopPropagation();
                                setIsDebugWindowOpen(false);
                            }}
                            sx={{ color: 'inherit' }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                <Table size="small">
                    <TableBody>
                        {dataRows.map((row, index) => (
                            <TableRow
                                key={row.label}
                                sx={{
                                    '&:nth-of-type(odd)': {
                                        backgroundColor: 'action.hover',
                                    },
                                    ...(index > 0 &&
                                        dataRows[index - 1].section !== row.section && {
                                            borderTop: '2px solid',
                                            borderColor: 'divider',
                                        }),
                                }}
                            >
                                <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                        py: 0.5,
                                        px: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 'medium',
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {row.label}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{
                                        py: 0.5,
                                        px: 1,
                                        fontSize: '0.75rem',
                                        fontFamily: 'monospace',
                                    }}
                                >
                                    {row.isStatus && row.value ? (
                                        <Chip
                                            label={formatValue(row.value)}
                                            size="small"
                                            color={getStatusColor(row.value as string | null)}
                                            sx={{ height: 18, fontSize: '0.65rem' }}
                                        />
                                    ) : (
                                        formatValue(row.value)
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    backgroundColor: 'action.hover',
                    px: 1,
                    py: 0.25,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    {t('debug.autoRefresh')}
                </Typography>
            </Box>
        </Paper>
    );
};

export default DebugDataWindow;
